import type { StandingRow } from "@aion2/shared";
import type { PublicCreator, PublicTeam } from "@/lib/api";

export const POST_PLAYER_CATEGORIES = [
  "progression",
  "pve",
  "pvp",
  "twitch",
  "warOrders",
  "wildCard",
] as const;
export type PostPlayerCategory = (typeof POST_PLAYER_CATEGORIES)[number];
export const PLAYER_STANDINGS_PAGE_SIZE = 8;

export type FinalPlayer = {
  id: string;
  rank: number;
  name: string | null;
  twitchLogin: string | null;
  teamId: string | null;
  teamName: string | null;
  faction: "elyos" | "asmodian" | null;
  region: string | null;
  points: number | null;
  scores: Record<PostPlayerCategory, number | null>;
};

export type FinalPlayerStandingsInput = {
  creatorRows: StandingRow[] | null;
  creators: PublicCreator[] | null;
  teams: PublicTeam[] | null;
};

const knownNumber = (value: number | null | undefined) =>
  typeof value === "number" && Number.isFinite(value) ? value : null;

/** Snapshot ranks are authoritative; roster order and missing identities cannot promote players. */
export function buildFinalPlayerStandings({
  creatorRows,
  creators,
  teams,
}: FinalPlayerStandingsInput) {
  const creatorById = new Map(
    (creators ?? []).map((creator) => [creator.id, creator]),
  );
  const teamById = new Map((teams ?? []).map((team) => [team.id, team]));
  const players: FinalPlayer[] = (creatorRows ?? []).map((row) => {
    const creator = creatorById.get(row.key);
    const team = creator ? teamById.get(creator.teamId) : undefined;
    const faction = team?.faction ?? creator?.faction;
    const categories = row.categories;
    return {
      id: row.key,
      rank: row.rank,
      name: creator?.displayName || row.label || null,
      twitchLogin: creator?.twitchLogin || null,
      teamId: creator?.teamId || null,
      teamName: team?.name || null,
      faction: faction === "elyos" || faction === "asmodian" ? faction : null,
      region: team?.region || creator?.region || null,
      points: knownNumber(row.points),
      scores: {
        progression: knownNumber(categories?.progression),
        pve: knownNumber(categories?.pve),
        pvp: knownNumber(categories?.pvp),
        twitch: knownNumber(categories?.twitch),
        warOrders: knownNumber(categories?.war_order),
        // Legacy snapshots predate the wildcard split. Entirely absent splits remain unknown.
        wildCard: categories
          ? knownNumber(
              categories.wildcard === undefined ? 0 : categories.wildcard,
            )
          : null,
      },
    };
  });
  players.sort(
    (a, b) =>
      a.rank - b.rank ||
      (b.points ?? -Infinity) - (a.points ?? -Infinity) ||
      a.id.localeCompare(b.id),
  );
  return {
    podium: players.filter((player) => player.rank >= 1 && player.rank <= 3),
    remaining: players.filter((player) => player.rank >= 4),
  };
}

export function playerStandingsPage<T>(
  players: readonly T[],
  requestedPage: number,
) {
  const totalPages = Math.max(
    1,
    Math.ceil(players.length / PLAYER_STANDINGS_PAGE_SIZE),
  );
  const page = Math.min(
    totalPages,
    Math.max(1, Math.trunc(requestedPage) || 1),
  );
  return {
    page,
    totalPages,
    items: players.slice(
      (page - 1) * PLAYER_STANDINGS_PAGE_SIZE,
      page * PLAYER_STANDINGS_PAGE_SIZE,
    ),
  };
}
